import apiClient from "./api";
import { clientId, msalInstance } from "../data/msalConfig"; // Adjust the import path as necessary
import { AUTH_ENABLED } from "../data/authConfig";
import { RIMSPolyline } from "../types/Polylines";
import { Point } from "../types/Point";
import { Polygon } from "../types/Polygon";
import { UpdateGeometryRequest } from "../types/Geometry";
import { GeometryStatus, QualityControl } from "../types/BaseGeometry";


const baseUrl = '/api/Geometry';

// Utility function to get a valid access token for the API
export const getApiAccessToken = async () => {
    if (!AUTH_ENABLED) return '';
    const accounts = msalInstance.getAllAccounts();
    if (!accounts || accounts.length === 0) {
        throw new Error('No accounts found. User may not be logged in.');
    }
    const request = {
        account: accounts[0],
        scopes: [`api://${clientId}/access_as_user`],
    };
    try {
        const response = await msalInstance.acquireTokenSilent(request);
        return response.accessToken;
    } catch (error) {
        console.error('Silent token acquisition failed, falling back to popup.', error);
        const response = await msalInstance.acquireTokenPopup(request);
        return response.accessToken;
    }
};

// Geometry routes

export const getPolylines = async (addressSystemId: number, contractId: number) => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`${baseUrl}/getAllPolylines/${addressSystemId}/${contractId}`, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    });
    if (response.status !== 200) {
        throw new Error(`Failed to fetch polylines: ${response.statusText}`);
    }
    //console.log("Fetched polylines:", response.data);
    return response.data as RIMSPolyline[];
};


export const getPolygons = async (addressSystemId: number, contractId: number) => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`${baseUrl}/getAllPolygons/${addressSystemId}/${contractId}`, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    });
    if (response.status !== 200) {
        throw new Error(`Failed to fetch polygons: ${response.statusText}`);
    }
    return response.data as Polygon[];
}


export const getPolypoints = async (addressSystemId: number, contractId: number) => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`${baseUrl}/getAllPolypoints/${addressSystemId}/${contractId}`, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    });
    if (response.status !== 200) {
        throw new Error(`Failed to fetch polypoints: ${response.statusText}`);
    }
    return response.data as Point[];
}

// Bulk geometry fetchers (across all contracts for an address system)
export const getAllPolylinesByAddressSystem = async (addressSystemId: number) => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`${baseUrl}/getAllPolylinesByAddressSystem/${addressSystemId}`, {
        headers: { Authorization: `Bearer ${token}` }
    });
    if (response.status !== 200) {
        throw new Error(`Failed to fetch all polylines: ${response.statusText}`);
    }
    return response.data as RIMSPolyline[];
};

export const getAllPolypointsByAddressSystem = async (addressSystemId: number) => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`${baseUrl}/getAllPolypointsByAddressSystem/${addressSystemId}`, {
        headers: { Authorization: `Bearer ${token}` }
    });
    if (response.status !== 200) {
        throw new Error(`Failed to fetch all polypoints: ${response.statusText}`);
    }
    return response.data as Point[];
};

export const getAllPolygonsByAddressSystem = async (addressSystemId: number) => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`${baseUrl}/getAllPolygonsByAddressSystem/${addressSystemId}`, {
        headers: { Authorization: `Bearer ${token}` }
    });
    if (response.status !== 200) {
        throw new Error(`Failed to fetch all polygons: ${response.statusText}`);
    }
    return response.data as Polygon[];
};

export const getContractsByAddressSystemAndUser = async (addressSystemId: number, userId: number) => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`${baseUrl}/contracts/${addressSystemId}/user/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
    });
    if (response.status !== 200) {
        throw new Error(`Failed to fetch contracts: ${response.statusText}`);
    }
    return response.data;
};

export const getStatusByAddressSystem = async (addressSystemId: number) => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`${baseUrl}/status/${addressSystemId}`, {
        headers: { Authorization: `Bearer ${token}` }
    });
    if (response.status !== 200) {
        throw new Error(`Failed to fetch status data: ${response.statusText}`);
    }
    return response.data as GeometryStatus[];
};

/**
 * Updates the colour of a specific status.
 * @param statusId - The ID of the status to update
 * @param colour - RGB colour array [R, G, B] where each value is 0-255
 * @returns The updated status data
 */
export const updateStatusColour = async (statusId: number, colour: number[]): Promise<GeometryStatus> => {
    const token = await getApiAccessToken();
    const response = await apiClient.put(`${baseUrl}/status/${statusId}/colour`, 
        { colour },
        { headers: { Authorization: `Bearer ${token}` } }
    );
    if (response.status !== 200) {
        throw new Error(`Failed to update status colour: ${response.statusText}`);
    }
    return response.data as GeometryStatus;
};

export const getTypeByAddressSystem = async (addressSystemId: number) => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`${baseUrl}/type/${addressSystemId}`, {
        headers: { Authorization: `Bearer ${token}` }
    });
    if (response.status !== 200) {
        throw new Error(`Failed to fetch type data: ${response.statusText}`);
    }
    return response.data;
};

export const getForemanByContract = async (contractId: number) => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`${baseUrl}/foreman/${contractId}`, {
        headers: { Authorization: `Bearer ${token}` }
    });
    if (response.status !== 200) {
        throw new Error(`Failed to fetch foreman data: ${response.statusText}`);
    }
    return response.data;
};

export const getContractorForemanByContract = async (contractId: number) => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`${baseUrl}/contractorForeman/${contractId}`, {
        headers: { Authorization: `Bearer ${token}` }
    });
    if (response.status !== 200) {
        throw new Error(`Failed to fetch contractor foreman data: ${response.statusText}`);
    }
    return response.data;
};

export const getQualityControlByAddressSystem = async (addressSystemId: number) => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`${baseUrl}/qualityControl/${addressSystemId}`, {
        headers: { Authorization: `Bearer ${token}` }
    });
    if (response.status !== 200) {
        throw new Error(`Failed to fetch quality control data: ${response.statusText}`);
    }
    return response.data as QualityControl[];
};

export const getQualityControl2ByAddressSystem = async (addressSystemId: number) => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`${baseUrl}/qualityControl2/${addressSystemId}`, {
        headers: { Authorization: `Bearer ${token}` }
    });
    if (response.status !== 200) {
        throw new Error(`Failed to fetch quality control 2 data: ${response.statusText}`);
    }
    return response.data as QualityControl[];
};

export const getGuaranteeFollowByAddressSystem = async (addressSystemId: number) => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`${baseUrl}/guaranteeFollow/${addressSystemId}`, {
        headers: { Authorization: `Bearer ${token}` }
    });
    if (response.status !== 200) {
        throw new Error(`Failed to fetch guarantee follow data: ${response.statusText}`);
    }
    return response.data;
};

export const getProjectManagersByContract = async (contractId: number) => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`${baseUrl}/projectManagers/${contractId}`, {
        headers: { Authorization: `Bearer ${token}` }
    });
    if (response.status !== 200) {
        throw new Error(`Failed to fetch project managers data: ${response.statusText}`);
    }
    return response.data;
};

export const getSubscribersByAddressSystem = async (addressSystemId: number) => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`${baseUrl}/subscribers/${addressSystemId}`, {
        headers: { Authorization: `Bearer ${token}` }
    });
    if (response.status !== 200) {
        throw new Error(`Failed to fetch subscribers data: ${response.statusText}`);
    }
    return response.data;
};

export const getRolesByAddressSystem = async (addressSystemId: number) => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`${baseUrl}/roles/${addressSystemId}`, {
        headers: { Authorization: `Bearer ${token}` }
    });
    if (response.status !== 200) {
        throw new Error(`Failed to fetch roles data: ${response.statusText}`);
    }
    return response.data;
};

export const createPolyline = async (payload: any) => {
    const token = await getApiAccessToken();
    const response = await apiClient.post(`${baseUrl}/polyline`, payload, {
        headers: { Authorization: `Bearer ${token}` }
    });
    if (response.status !== 201 && response.status !== 200) {
        throw new Error(`Failed to create polyline: ${response.statusText}`);
    }
    return response.data;
};

export const createPolypoint = async (payload: any) => {
    const token = await getApiAccessToken();
    const response = await apiClient.post(`${baseUrl}/polypoint`, payload, {
        headers: { Authorization: `Bearer ${token}` }
    });
    if (response.status !== 201 && response.status !== 200) {
        throw new Error(`Failed to create polypoint: ${response.statusText}`);
    }
    return response.data;
};

export const createPolygon = async (payload: any) => {
    const token = await getApiAccessToken();
    const response = await apiClient.post(`${baseUrl}/polygon`, payload, {
        headers: { Authorization: `Bearer ${token}` }
    });
    if (response.status !== 201 && response.status !== 200) {
        throw new Error(`Failed to create polygon: ${response.statusText}`);
    }
    return response.data;
};

export const updatePolyline = async (payload: UpdateGeometryRequest) => {
    const token = await getApiAccessToken();
    const response = await apiClient.put(`${baseUrl}/polyline`, payload, {
        headers: { Authorization: `Bearer ${token}` }
    });
    if (response.status !== 200) {
        throw new Error(`Failed to update polyline: ${response.statusText}`);
    }
    return response.data;
};

export const updatePolypoint = async (payload: UpdateGeometryRequest) => {
    const token = await getApiAccessToken();
    const response = await apiClient.put(`${baseUrl}/polypoint`, payload, {
        headers: { Authorization: `Bearer ${token}` }
    });
    if (response.status !== 200) {
        throw new Error(`Failed to update polypoint: ${response.statusText}`);
    }
    return response.data;
};

export const updatePolygon = async (payload: UpdateGeometryRequest) => {
    const token = await getApiAccessToken();
    const response = await apiClient.put(`${baseUrl}/polygon`, payload, {
        headers: { Authorization: `Bearer ${token}` }
    });
    if (response.status !== 200) {
        throw new Error(`Failed to update polygon: ${response.statusText}`);
    }
    return response.data;
};

export const downloadFile = async (fileId: number, fileName: string) => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`${baseUrl}/file/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
    });
    
    if (response.status !== 200) {
        throw new Error(`Failed to download file: ${response.statusText}`);
    }
    
    const blob = response.data as Blob;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

export const deleteFileById = async (fileId: number) => {
    const token = await getApiAccessToken();
    const response = await apiClient.delete(`${baseUrl}/file/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
    });
    
    if (response.status !== 204) {
        throw new Error(`Failed to delete file: ${response.statusText}`);
    }
};

export const copyObjectToContract = async (payload: { perceptionId: number; targetContractId: number; newName: string; userId: number }) => {
    const token = await getApiAccessToken();
    const response = await apiClient.post(`${baseUrl}/copyToContract`, payload, {
        headers: { Authorization: `Bearer ${token}` }
    });
    
    if (response.status !== 200) {
        throw new Error(`Failed to copy object to contract: ${response.statusText}`);
    }
    
    return response.data;
};